import {getTestProps} from '@benchling/js-test-utils';
import {css, StyleSheet} from 'aphrodite';
import React, {useEffect, useState} from 'react';

import Tooltip from 'ui-library/bs/Tooltip';
import FieldError from 'ui-library/FieldError';
import useThrottle from 'ui-library/hooks/useThrottle';
import WarningIcon from 'ui-library/icons/WarningIcon';
import {IntermediateValueControlledNumericInput} from 'ui-library/inputs/ControlledNumericInput';
import FieldLabel from 'ui-library/inputs/FieldLabel';

type AxisRangeParameters<TParameters, MinKey extends string, MaxKey extends string> = TParameters &
  Record<MinKey | MaxKey, number | null | undefined>;

export interface AxisRangeConfigEditorProps<
  TParameters extends Record<string, unknown>,
  MinKey extends string = string,
  MaxKey extends string = string,
> {
  axisLabel: string;
  minKey: MinKey;
  maxKey: MaxKey;
  parameters: AxisRangeParameters<TParameters, MinKey, MaxKey>;
  onUpdateParameters: (update: Partial<TParameters>) => void;
  warning?: string | null;
}

/** @package */
export const THROTTLE_INTERVAL = 300;

export default function AxisRangeConfigEditor<
  TParameters extends Record<string, unknown>,
  MinKey extends string = string,
  MaxKey extends string = string,
>({
  axisLabel,
  minKey,
  maxKey,
  parameters,
  onUpdateParameters,
  warning,
}: AxisRangeConfigEditorProps<TParameters, MinKey, MaxKey>): JSX.Element {
  const [draftMin, setDraftMin] = useState<number | null>(parameters[minKey] ?? null);
  const [draftMax, setDraftMax] = useState<number | null>(parameters[maxKey] ?? null);

  function handleOnChangeMin(newMin: number | null): void {
    setDraftMin(newMin);
  }

  function handleOnChangeMax(newMax: number | null): void {
    setDraftMax(newMax);
  }

  const throttledDraftMin = useThrottle(draftMin, THROTTLE_INTERVAL);
  const throttledDraftMax = useThrottle(draftMax, THROTTLE_INTERVAL);

  useEffect(() => {
    // If one of the parameter values has changed
    if (parameters[minKey] !== throttledDraftMin || parameters[maxKey] !== throttledDraftMax) {
      // If the parameter values are valid (i.e., either one or more value is null, or else min < max),
      // then update the parameters
      if (throttledDraftMin == null || throttledDraftMax == null || throttledDraftMin < throttledDraftMax) {
        onUpdateParameters({
          [minKey]: throttledDraftMin,
          [maxKey]: throttledDraftMax,
        } as Partial<TParameters>);
      }
    }
  }, [onUpdateParameters, parameters, minKey, maxKey, throttledDraftMin, throttledDraftMax]);

  return (
    <div
      className="u-input-spacing-top"
      {...getTestProps({component: 'AxisRangeConfigEditor', element: 'root', key: axisLabel})}
    >
      <FieldLabel
        label={
          <>
            {axisLabel}
            {warning && (
              <Tooltip title={warning}>
                <span aria-label={warning}>
                  <WarningIcon className="u-tiny-spacing-left" />
                </span>
              </Tooltip>
            )}
          </>
        }
      />

      <div className={css(styles.numericInputsWrapper)}>
        <IntermediateValueControlledNumericInput
          className="u-mini-spacing-left"
          onChange={(e) => {
            handleOnChangeMin(e.target.numericValue);
          }}
          placeholder="Min"
          value={draftMin}
        />
        <IntermediateValueControlledNumericInput
          className="u-mini-spacing-left"
          onChange={(e) => {
            handleOnChangeMax(e.target.numericValue);
          }}
          placeholder="Max"
          value={draftMax}
        />
      </div>
      {draftMin != null && draftMax != null && draftMax <= draftMin && (
        <FieldError message={`Max needs to be greater than ${draftMin}`} />
      )}
    </div>
  );
}

const styles = StyleSheet.create({
  numericInputsWrapper: {
    display: 'flex',
  },
});
